/**
* Test Runner Page
* Sida för att köra och hantera tester
*/
import { useState, useEffect } from'react';
import { useTranslation } from '@/store/languageStore';
import { PageBackButton } from '@/components/common/PageBackButton';
import {
runTests,
getTestResults,
getTestResult,
deleteTestResult,
deleteAllTestResults,
type TestResult,
type TestRunRequest,
} from'@/services/api/testingService';
import {
PlayIcon,
TrashIcon,
CheckCircleIcon,
XCircleIcon,
ClockIcon,
ClipboardDocumentIcon,
ExclamationTriangleIcon,
BeakerIcon,
} from'@heroicons/react/24/outline';
const TestRunner = () => {
const { t } = useTranslation();
const [results, setResults] = useState<TestResult[]>([]);
const [loading, setLoading] = useState(false);
const [_runningTests, setRunningTests] = useState<Set<string>>(new Set());
const [selectedResult, setSelectedResult] = useState<TestResult | null>(null);
const [testPattern, setTestPattern] = useState('');
const [testFile, setTestFile] = useState('');
const [runAll, setRunAll] = useState(false);
const [copySuccess, setCopySuccess] = useState(false);
const [copyErrorsSuccess, setCopyErrorsSuccess] = useState(false);
// Ladda testresultat vid mount och uppdatera var 5:e sekund
useEffect(() => {
loadResults();
const interval = setInterval(() => {
loadResults();
// Uppdatera running tests
results
.filter(r => r.status ==='running')
.forEach(result => {
refreshResult(result.id);
});
}, 5000);
return () => clearInterval(interval);
}, []);
const loadResults = async () => {
try {
const testResults = await getTestResults();
setResults(testResults);
// Uppdatera running tests
const running = testResults.filter(r => r.status ==='running').map(r => r.id);
setRunningTests(new Set(running));
} catch {
// Error loading test results
}
};
const refreshResult = async (id: string) => {
try {
const result = await getTestResult(id);
setResults(prev => prev.map(r => r.id === id ? result : r));
if (result.status ==='running') {
setRunningTests(prev => new Set(prev).add(id));
} else {
setRunningTests(prev => {
const next = new Set(prev);
next.delete(id);
return next;
});
}
} catch {
// Error refreshing test result
}
};
const handleRunTests = async () => {
if (!testPattern && !testFile && !runAll) {
alert(t('copy.admin.testRunnerPatternRequired'));
return;
}
setLoading(true);
try {
const request: TestRunRequest = {};
if (runAll) {
request.all = true;
} else if (testFile) {
request.testFile = testFile;
} else if (testPattern) {
request.testPattern = testPattern;
}
const { id } = await runTests(request);
setRunningTests(prev => new Set(prev).add(id));
// Ladda resultat efter kort delay
setTimeout(() => {
loadResults();
}, 1000);
// Rensa input
setTestPattern('');
setTestFile('');
setRunAll(false);
} catch (error: any) {
alert(t('copy.admin.testRunnerStartFailed', { message: error.message }));
} finally {
setLoading(false);
}
};
const handleDeleteResult = async (id: string) => {
if (!confirm(t('copy.admin.testRunnerDeleteOneConfirm'))) {
return;
}
try {
await deleteTestResult(id);
setResults(prev => prev.filter(r => r.id !== id));
if (selectedResult?.id === id) {
setSelectedResult(null);
}
} catch {
alert('Kunde inte ta bort testresultat');
}
};
const handleDeleteAll = async () => {
if (!confirm(t('copy.admin.testRunnerDeleteAllConfirm'))) {
return;
}
try {
await deleteAllTestResults();
setResults([]);
setSelectedResult(null);
} catch {
alert('Kunde inte ta bort testresultat');
}
};
const formatDuration = (ms: number): string => {
if (ms < 1000) return `${ms}ms`;
return `${(ms / 1000).toFixed(2)}s`;
};
const formatDate = (timestamp: string): string => {
return new Date(timestamp).toLocaleString('sv-SE');
};
const copyToClipboard = async (text: string): Promise<boolean> => {
// Försök använda modern Clipboard API först
if (navigator.clipboard && navigator.clipboard.writeText) {
try {
await navigator.clipboard.writeText(text);
return true;
} catch {
// Clipboard API failed - trying fallback
}
}
// Fallback: Använd gammal metod med textarea
const textarea = document.createElement('textarea');
textarea.value = text;
textarea.style.position ='fixed';
textarea.style.left ='-999999px';
textarea.style.top ='-999999px';
document.body.appendChild(textarea);
textarea.focus();
textarea.select();
try {
const successful = document.execCommand('copy');
document.body.removeChild(textarea);
return successful;
} catch {
document.body.removeChild(textarea);
return false;
}
};
const handleCopyAll = async () => {
if (!selectedResult) return;
try {
// Skapa både JSON och läsbar textversion
const jsonText = JSON.stringify(selectedResult, null, 2);
// Skapa läsbar textversion
let readableText = `${t('copy.admin.testRunnerResultHeading', { name: selectedResult.testPattern || selectedResult.testFile || t('copy.admin.testRunnerAllTests') })}\n`;
readableText += `${t('copy.admin.testRunnerStatus')}: ${selectedResult.status}\n`;
readableText += `${t('copy.admin.testRunnerTimestamp')}: ${formatDate(selectedResult.timestamp)}\n\n`;
if (selectedResult.summary) {
readableText += `${t('copy.admin.testRunnerSummary')}:\n`;
readableText += ` ${t('copy.admin.testRunnerTotal')}: ${selectedResult.summary.total} ${t('copy.admin.testRunnerTests')}\n`;
readableText += ` ${t('copy.admin.testRunnerPassed')}: ${selectedResult.summary.passed}\n`;
readableText += ` ${t('copy.admin.testRunnerFailed')}: ${selectedResult.summary.failed}\n`;
readableText += ` ${t('copy.admin.testRunnerDuration')}: ${formatDuration(selectedResult.summary.duration)}\n\n`;
}
if (selectedResult.tests && selectedResult.tests.length > 0) {
readableText += `${t('copy.admin.testRunnerTestDetails')}:\n`;
selectedResult.tests.forEach((test) => {
readableText += ` ${test.status ==='passed' ?'✓' :'✗'} ${test.name}`;
if (test.duration) {
readableText += ` (${formatDuration(test.duration)})`;
}
readableText +='\n';
if (test.error) {
readableText += ` ${t('copy.admin.testRunnerError')}: ${test.error}\n`;
}
});
readableText +='\n';
}
if (selectedResult.error) {
readableText += `${t('copy.admin.testRunnerError')}:\n${selectedResult.error}\n\n`;
}
if (selectedResult.output) {
readableText += `Output:\n${selectedResult.output}\n\n`;
}
// Kombinera både läsbar text och JSON
const textToCopy = `${readableText}\n--- JSON ---\n${jsonText}`;
const success = await copyToClipboard(textToCopy);
if (success) {
setCopySuccess(true);
setTimeout(() => setCopySuccess(false), 2000);
} else {
alert(t('copy.admin.testRunnerCopyFallback', { text: textToCopy.substring(0, 500) }));
}
} catch {
alert(t('copy.admin.testRunnerClipboardFailed'));
}
};
const handleCopyErrors = async () => {
if (!selectedResult) return;
// Hämta bara misslyckade tester
const failedTests = selectedResult.tests?.filter(test => test.status ==='failed') || [];
if (failedTests.length === 0) {
alert(t('copy.admin.testRunnerNoFailedToCopy'));
return;
}
try {
// Skapa kompakt text med bara felinformation
let errorText = `${t('copy.admin.testRunnerResultHeading', { name: selectedResult.testPattern || selectedResult.testFile || t('copy.admin.testRunnerAllTests') })}\n`;
errorText += `${t('copy.admin.testRunnerStatus')}: ${selectedResult.status}\n`;
errorText += `${t('copy.admin.testRunnerTimestamp')}: ${formatDate(selectedResult.timestamp)}\n\n`;
if (selectedResult.summary) {
errorText += `${t('copy.admin.testRunnerSummary')}:\n`;
errorText += ` ${t('copy.admin.testRunnerTotal')}: ${selectedResult.summary.total} ${t('copy.admin.testRunnerTests')}\n`;
errorText += ` ${t('copy.admin.testRunnerPassed')}: ${selectedResult.summary.passed}\n`;
errorText += ` ${t('copy.admin.testRunnerFailed')}: ${selectedResult.summary.failed}\n`;
errorText += ` ${t('copy.admin.testRunnerDuration')}: ${formatDuration(selectedResult.summary.duration)}\n\n`;
}
errorText += `${t('copy.admin.testRunnerFailedTests', { count: failedTests.length })}:\n\n`;
failedTests.forEach((test, index) => {
errorText += `${index + 1}. ${test.name}\n`;
if (test.duration) {
errorText += ` ${t('copy.admin.testRunnerDuration')}: ${formatDuration(test.duration)}\n`;
}
if (test.error) {
errorText += ` ${t('copy.admin.testRunnerError')}:\n${test.error}\n`;
}
errorText +='\n';
});
// Lägg till eventuellt huvudfel (om test suite misslyckades)
if (selectedResult.error) {
errorText += `\n${t('copy.admin.testRunnerSuiteError')}:\n${selectedResult.error}\n\n`;
}
// Lägg till relevant output (bara om det finns fel)
if (selectedResult.output) {
// Extrahera bara fel-relaterad output
const outputLines = selectedResult.output.split('\n');
const errorLines = outputLines.filter(line => line.includes('FAIL') || line.includes('Error') || line.includes('✕') || line.includes('●') ||
line.includes('expected') ||
line.includes('received')
);
if (errorLines.length > 0) {
errorText += `\nRelevant Output:\n${errorLines.join('\n')}\n`;
}
}
const success = await copyToClipboard(errorText);
if (success) {
setCopyErrorsSuccess(true);
setTimeout(() => setCopyErrorsSuccess(false), 2000);
} else {
alert(t('copy.admin.testRunnerCopyFallback', { text: errorText.substring(0, 500) }));
}
} catch {
alert(t('copy.admin.testRunnerClipboardFailed'));
}
};
return (
<div className="p-6 space-y-6">
<div className="flex justify-between items-center">
<div className="flex items-center gap-3">
<PageBackButton />
<h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
<BeakerIcon className="w-6 h-6 text-primary"/>
Test Runner
</h1>
</div>
{results.length > 0 && (
<button
onClick={handleDeleteAll}
className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 flex items-center gap-2"
>
<TrashIcon className="w-4 h-4"/>
{t('copy.admin.testRunnerDeleteAll')}
</button>
)}
</div>
{/* Run Tests Form */}
<div className="bg-bgElevated p-6 rounded-lg shadow">
<h2 className="text-xl font-semibold mb-4">{t('copy.admin.testRunnerRunTests')}</h2>
<div className="space-y-4">
<div>
<label className="block text-sm font-medium mb-2">
Test Pattern (ex:"auth","authService")
</label>
<input
type="text"
value={testPattern}
onChange={(e) => {
setTestPattern(e.target.value);
setTestFile('');
setRunAll(false);
}}
placeholder="auth"
className="w-full px-3 py-2 border rounded"
disabled={loading || runAll || !!testFile}
/>
</div>
<div>
<label className="block text-sm font-medium mb-2">
Test File (ex:"authService.test.ts")
</label>
<input
type="text"
value={testFile}
onChange={(e) => {
setTestFile(e.target.value);
setTestPattern('');
setRunAll(false);
}}
placeholder="authService.test.ts"
className="w-full px-3 py-2 border rounded"
disabled={loading || runAll || !!testPattern}
/>
</div>
<div className="flex items-center">
<input
type="checkbox"
id="runAll"
checked={runAll}
onChange={(e) => {
setRunAll(e.target.checked);
setTestPattern('');
setTestFile('');
}}
className="mr-2"
disabled={loading || !!testPattern || !!testFile}
/>
<label htmlFor="runAll"className="text-sm font-medium">
{t('copy.admin.testRunnerRunAll')}
</label>
</div>
<button
onClick={handleRunTests}
disabled={loading || (!testPattern && !testFile && !runAll)}
className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
>
<PlayIcon className="w-4 h-4"/>
{loading ? t('copy.admin.testRunnerStarting') : t('copy.admin.testRunnerRunTests')}
</button>
</div>
</div>
{/* Test Results List */}
<div className="bg-bgElevated p-6 rounded-lg shadow">
<h2 className="text-xl font-semibold mb-4">{t('copy.admin.testRunnerResults')}</h2>
{results.length === 0 ? (
<p className="text-textSecondary">{t('copy.admin.testRunnerNoResults')}</p>
) : (
<div className="space-y-2">
{results.map((result) => (
<div
key={result.id}
className={`p-4 border rounded cursor-pointer hover:bg-bgBase ${
selectedResult?.id === result.id ?'bg-blue-50 dark:bg-blue-900' :''
}`}
onClick={() => setSelectedResult(result)}
>
<div className="flex justify-between items-start">
<div className="flex-1">
<div className="flex items-center gap-2 mb-2">
{result.status ==='running' && (
<ClockIcon className="w-5 h-5 text-yellow-500 animate-spin"/>
)}
{result.status ==='completed' && (
<CheckCircleIcon className="w-5 h-5 text-green-500"/>
)}
{result.status ==='failed' && (
<XCircleIcon className="w-5 h-5 text-red-500"/>
)}
<span className="font-semibold">
{result.testPattern || result.testFile || t('copy.admin.testRunnerAllTests')}
</span>
<span className="text-sm text-textSecondary">
{formatDate(result.timestamp)}
</span>
</div>
{result.summary && (
<div className="text-sm text-textSecondary">
{t('copy.admin.testRunnerPassedFraction', { passed: result.summary.passed, total: result.summary.total })}
{result.summary.failed > 0 && (
<span className="text-red-500 ml-2">
{t('copy.admin.testRunnerFailedCount', { count: result.summary.failed })}
</span>
)}
{result.summary.duration > 0 && (
<span className="ml-2">
({formatDuration(result.summary.duration)})
</span>
)}
</div>
)}
</div>
<button
onClick={(e) => {
e.stopPropagation();
handleDeleteResult(result.id);
}}
className="p-2 text-red-500 hover:bg-red-50 rounded"
title={t('common.delete')}
>
<TrashIcon className="w-4 h-4"/>
</button>
</div>
</div>
))}
</div>
)}
</div>
{/* Test Result Details */}
{selectedResult && (
<div className="bg-bgElevated p-6 rounded-lg shadow">
<div className="flex justify-between items-center mb-4">
<h2 className="text-xl font-semibold">{t('copy.admin.testRunnerDetails')}</h2>
<div className="flex items-center gap-2">
{selectedResult.summary && selectedResult.summary.failed > 0 && (
<button
onClick={handleCopyErrors}
className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 flex items-center gap-2 text-sm"
title={t('copy.admin.testRunnerCopyErrorsTitle')}
>
<ExclamationTriangleIcon className="w-4 h-4"/>
{copyErrorsSuccess ? t('copy.admin.testRunnerCopied') : t('copy.admin.testRunnerCopyErrors')}
</button>
)}
<button
onClick={handleCopyAll}
className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-2 text-sm"
title={t('copy.admin.testRunnerCopyAllTitle')}
>
<ClipboardDocumentIcon className="w-4 h-4"/>
{copySuccess ? t('copy.admin.testRunnerCopied') : t('copy.admin.testRunnerCopyAll')}
</button>
<button
onClick={() => setSelectedResult(null)}
className="text-textSecondary hover:text-textDefault px-2 py-1"
>
✕
</button>
</div>
</div>
<div className="space-y-4">
<div>
<strong>{t('copy.admin.testRunnerStatus')}:</strong> {selectedResult.status}
</div>
<div>
<strong>{t('copy.admin.testRunnerTimestamp')}:</strong> {formatDate(selectedResult.timestamp)}
</div>
{selectedResult.summary && (
<div>
<strong>{t('copy.admin.testRunnerSummary')}:</strong>
<ul className="list-disc list-inside ml-4">
<li>{t('copy.admin.testRunnerTotal')}: {selectedResult.summary.total} {t('copy.admin.testRunnerTests')}</li>
<li className="text-green-600">{t('copy.admin.testRunnerPassed')}: {selectedResult.summary.passed}</li>
<li className="text-red-600">{t('copy.admin.testRunnerFailed')}: {selectedResult.summary.failed}</li>
<li>{t('copy.admin.testRunnerDuration')}: {formatDuration(selectedResult.summary.duration)}</li>
</ul>
</div>
)}
{selectedResult.tests && selectedResult.tests.length > 0 && (
<div>
<strong>{t('copy.admin.testRunnerTestDetails')}:</strong>
<div className="mt-2 space-y-1 max-h-96 overflow-y-auto">
{selectedResult.tests.map((test, index) => (
<div
key={index}
className={`p-2 rounded ${
test.status ==='passed'
?'bg-green-50 dark:bg-green-900'
:'bg-red-50 dark:bg-red-900'
}`}
>
<div className="flex items-center gap-2">
{test.status ==='passed' ? (
<CheckCircleIcon className="w-4 h-4 text-green-500"/>
) : (
<XCircleIcon className="w-4 h-4 text-red-500"/>
)}
<span className="font-medium">{test.name}</span>
{test.duration && (
<span className="text-xs text-textSecondary">
({formatDuration(test.duration)})
</span>
)}
</div>
{test.error && (
<pre className="mt-1 text-xs text-red-600 dark:text-red-400 whitespace-pre-wrap">
{test.error}
</pre>
)}
</div>
))}
</div>
</div>
)}
{selectedResult.error && (
<div>
<strong className="text-red-600">{t('copy.admin.testRunnerError')}:</strong>
<pre className="mt-1 p-2 bg-red-50 dark:bg-red-900 rounded text-sm whitespace-pre-wrap">
{selectedResult.error}
</pre>
</div>
)}
{selectedResult.output && (
<div>
<strong>Output:</strong>
<pre className="mt-1 p-2 bg-bgLight rounded text-sm whitespace-pre-wrap max-h-96 overflow-y-auto">
{selectedResult.output}
</pre>
</div>
)}
</div>
</div>
)}
</div>
);
};
export default TestRunner;
